// File: frontend/src/pages/fees/Defaulters.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/axios';
import PageHeader from '../../components/PageHeader';

const Defaulters = () => {
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [defaulters, setDefaulters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch fee defaulters from backend API
   */
  const fetchDefaulters = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/api/v1/fees/defaulters', {
        params: {
          class_id: selectedClass,
          month: selectedMonth,
          year: selectedYear
        }
      });

      if (response.data?.success) {
        setDefaulters(response.data.data?.defaulters || []);
      } else {
        setError(response.data?.message || 'Failed to fetch fee defaulters');
      }
    } catch (err) {
      console.error('Error fetching fee defaulters:', err);
      setError(err.response?.data?.message || err.message || 'Error communicating with server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDefaulters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedMonth, selectedYear]);

  const handleExportCSV = () => {
    if (!defaulters || defaulters.length === 0) {
      alert('No defaulters data available to export.');
      return;
    }
    const csv = [
      ['Student ID', 'Name', 'Class', 'Amount Due', 'Status'],
      ...defaulters.map(d => [
        d.student_id,
        d.student_name,
        d.class_name || `Class ${d.class_id}`,
        d.balance || d.balance_amount,
        d.status || 'Pending'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'defaulters.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsSentCount, setSmsSentCount] = useState(0);

  const handleSendSMS = () => {
    if (!defaulters || defaulters.length === 0) return;
    setSmsSentCount(defaulters.length);
    setSmsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Page Navigation Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <PageHeader
          title="Fee Defaulters Directory"
          subtitle="Identify and notify students with outstanding pending fee dues"
          breadcrumbs={[
            { label: 'Fees', path: '/fees' },
            { label: 'Defaulters' }
          ]}
          backUrl="/fees"
        >
          <button
            onClick={handleExportCSV}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-xl text-xs transition"
          >
            Export CSV
          </button>
          <button
            onClick={handleSendSMS}
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-xl text-xs transition shadow-sm flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            Send SMS Reminders
          </button>
        </PageHeader>
      </div>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        
        {/* Filter Controls */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            
            {/* Filter Class */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Class</label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Classes</option>
                <option value="1">Class 1</option>
                <option value="2">Class 2</option>
                <option value="3">Class 3</option>
                <option value="4">Class 4</option>
                <option value="5">Class 5</option>
                <option value="6">Class 6</option>
                <option value="7">Class 7</option>
                <option value="8">Class 8</option>
                <option value="9">Class 9</option>
                <option value="10">Class 10</option>
                <option value="11">Class 11</option>
                <option value="12">Class 12</option>
              </select>
            </div>

            {/* Filter Month */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value={1}>January</option>
                <option value={2}>February</option>
                <option value={3}>March</option>
                <option value={4}>April</option>
                <option value={5}>May</option>
                <option value={6}>June</option>
                <option value={7}>July</option>
                <option value={8}>August</option>
                <option value={9}>September</option>
                <option value={10}>October</option>
                <option value={11}>November</option>
                <option value={12}>December</option>
              </select>
            </div>

            {/* Filter Year */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
                <option value={2027}>2027</option>
              </select>
            </div>

          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-800">✕</button>
          </div>
        )}

        {/* Defaulters Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-bold text-slate-800">
              Fee Defaulters • Month {selectedMonth}/{selectedYear}
            </div>
            <div className="text-xs font-bold text-red-700 bg-red-50 px-3 py-1 rounded-full border border-red-200">
              {defaulters.length} Students Pending Payment
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-3.5">Student ID</th>
                  <th className="px-6 py-3.5">Student Name</th>
                  <th className="px-6 py-3.5">Class</th>
                  <th className="px-6 py-3.5">Father / Contact Phone</th>
                  <th className="px-6 py-3.5 text-right">Amount Due (Rs.)</th>
                  <th className="px-6 py-3.5 text-right">Last Payment</th>
                  <th className="px-6 py-3.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm font-medium text-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-400">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-8 h-8 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Loading fee defaulters...</span>
                      </div>
                    </td>
                  </tr>
                ) : defaulters.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-emerald-600 font-semibold">
                      ✓ Great news! No fee defaulters found for Month {selectedMonth}/{selectedYear}.
                    </td>
                  </tr>
                ) : (
                  defaulters.map((item) => (
                    <tr key={item.student_id} className="hover:bg-slate-50/80 transition">
                      <td className="px-6 py-4 font-mono font-bold text-slate-800 text-xs">
                        {item.student_code}
                      </td>
                      <td className="px-6 py-4 font-bold text-slate-900">
                        {item.student_name}
                      </td>
                      <td className="px-6 py-4 font-semibold text-slate-700">
                        Class {item.class_id}
                      </td>
                      <td className="px-6 py-4 text-xs text-slate-600">
                        <div>{item.father_name}</div>
                        <div className="text-slate-400 font-mono">{item.phone}</div>
                      </td>
                      <td className="px-6 py-4 text-right font-mono font-extrabold text-red-600">
                        Rs. {parseFloat(item.balance_amount).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                      </td>
                      <td className="px-6 py-4 text-right text-xs text-slate-500">
                        {item.last_payment_date ? new Date(item.last_payment_date).toLocaleDateString() : 'Never Paid'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link
                          to="/fees/collect"
                          className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold rounded-lg text-xs transition"
                        >
                          Collect Fee
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>

      {/* SMS Dispatch Modal */}
      {smsModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 text-center space-y-4">
            <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-2xl font-bold">
              📱
            </div>
            <h3 className="text-xl font-bold text-slate-800">Fee Reminder SMS Dispatched</h3>
            <p className="text-slate-600 text-sm">
              Automated SMS payment reminders have been generated and sent to <span className="font-bold text-slate-900">{smsSentCount} parent contacts</span>.
            </p>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-left text-xs font-mono text-slate-700">
              "Dear Parent, tuition fee balance is outstanding for your child at SchoolMS. Please settle dues at your earliest convenience."
            </div>
            <button
              onClick={() => setSmsModalOpen(false)}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-sm transition"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Defaulters;
